import { useInfiniteQuery } from "@tanstack/react-query";
import { fetchUserClickAnalyticsWithParams } from "../services";
import { useEffect, useMemo, useState, useCallback } from "react";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import weekOfYear from "dayjs/plugin/weekOfYear";
import advancedFormat from "dayjs/plugin/advancedFormat";
import isoWeek from "dayjs/plugin/isoWeek";
import functions from "../utils/functions";

dayjs.extend(isBetween);
dayjs.extend(advancedFormat);
dayjs.extend(weekOfYear);
dayjs.extend(isoWeek);


const { splitAndFirstLetterOnlyToUpperCase } = functions;

/**
 * Base aggregate click analytics query hook
 * @param {*} param0
 * @returns
 */
export const useClickAnalyticsData = ({ ...filters }) => {
  const {
    data: pages,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey: ["clickAnalytics", { ...filters }],
    queryFn: async ({ pageParam = 0 }) => {
      const { dateRange } = filters;
      const response = await fetchUserClickAnalyticsWithParams({
        from: dateRange.at(0).toISOString(),
        to: dateRange.at(1).toISOString(),
        size: 150,
        page: pageParam,
      });

      const clickData =
        response?._embedded?.clickAnalyticsDtoList.map((it) => ({
          ...it,
          value: 1,
        })) || [];

      return {
        clickData,
        page: response?.page,
        totalRecords: response?.page?.totalElements || 0,
      };
    },
    getNextPageParam: (lastPage, allPages) => {
      const total = lastPage.page.totalElements || 0;
      const loaded = allPages.reduce((acc, p) => acc + p.clickData.length, 0);
      const currentPage = lastPage?.page?.number || 0;
      return loaded < total ? currentPage + 1 : undefined;
    },
    initialPageParam: 0,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const allClickData = useMemo(
    () => pages?.pages?.flatMap((p) => p.clickData) || [],
    [pages]
  );

  return {
    clickData: allClickData,
    isLoading: isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
  };
};

export const useEngagementChartData = ({ clickData, filters }) => {
  const [period, setPeriod] = useState("Day");
  const [chartType, setChartType] = useState("bar");
  const [isStacked, setIsStacked] = useState(true);
  const [groupBy, setGroupBy] = useState("condition");
  const [visibleDateRange, setVisibleDateRange] = useState(filters.dateRange);

  const keyFromItem = (item, groupBy) => {
    switch (groupBy) {
      case "condition":
        return item.conditionName || "(Unspecified)";
      case "designation":
        return splitAndFirstLetterOnlyToUpperCase(item?.user?.designation, "_") || "(Unspecified)";
      case "hospital":
        return item?.user?.hospital?.name || "(No hospital)";
      case "district":
        return item?.user?.district?.name || "(No district)";
      case "gender":
        return (item?.user?.gender || "(No gender)").toUpperCase();
      default:
        return "(Unknown)";
    }
  };

  const chartData = useMemo(() => {
    if (!clickData?.length) return { buckets: [], series: [] };

    const [from, to] = visibleDateRange.map(dayjs);

    const periodConfig = {
      Day: { unit: "day", format: "MMM D" },
      Week: { unit: "week", format: "MMM D" },
      Month: { unit: "month", format: "MMM 'YY" },
      Year: { unit: "year", format: "YYYY" },
    }[period];

    const buckets = Array.from(
      { length: to.diff(from, periodConfig.unit) + 1 },
      (_, i) => {
        const date = from.add(i, periodConfig.unit).startOf(periodConfig.unit);
        return { label: date.format(periodConfig.format), date };
      }
    );

    const seriesMap = new Map();

    clickData.forEach((item) => {
      const ts = dayjs(item.timeStamp);
      const bucketIndex = buckets.findIndex((b) =>
        ts.isSame(b.date, period.toLowerCase())
      );
      if (bucketIndex < 0) return;

      const key = keyFromItem(item, groupBy);

      if (!seriesMap.has(key)) {
        seriesMap.set(key, new Array(buckets.length).fill(0));
      }

      seriesMap.get(key)[bucketIndex] += item.value || 1;
    });

    const series = Array.from(seriesMap.entries()).map(([name, data]) => ({
      name,
      type: chartType,
      stack: isStacked && chartType === "bar" ? "total" : undefined,
      label: {
        show: true,
        position:
          chartType === "bar" ? (isStacked ? "inside" : "top") : "right",
      },
      // Replace zeros with null for better visualization
      data: data.map((v) => (v === 0 ? null : v)),
      emphasis: {
        focus: "series",
      },
      animation: true,
    }));


    return {
      buckets: buckets.map((b) => b.label),
      series,
      rawData: clickData,
    };
  }, [clickData, visibleDateRange, groupBy, chartType, isStacked, period]);

  // Zoom handler
    const handleZoom = useCallback((newRange) => {
      setVisibleDateRange(newRange);
    }, []);

    // Reset zoom to full range
    const resetZoom = useCallback(() => {
      setVisibleDateRange(filters.dateRange);
    }, [filters.dateRange]);

  return {
    chartData,
    period,
    setPeriod,
    chartType,
    setChartType,
    isStacked,
    setIsStacked,
    groupBy,
    setGroupBy,
    handleZoom,
    resetZoom,
    setVisibleDateRange,
    isZoomed:
      !dayjs(visibleDateRange[0]).isSame(filters.dateRange[0]) ||
      !dayjs(visibleDateRange[1]).isSame(filters.dateRange[1]),
  };
};


export const useEngagementRadarData = ({ clickData, filters }) => {
  const { designations, conditions: selectedConditions } = filters;

  const radarData = useMemo(() => {
    const conditionSet = new Set();
    const map = {};

    for (const item of clickData) {
      const cond = item.conditionName || "Other";
      const desig = splitAndFirstLetterOnlyToUpperCase(
        item.user?.designation,
        "_"
      );

      if (
        (selectedConditions?.length && !selectedConditions.includes(cond)) ||
        (designations?.length && !designations.includes(desig))
      )
        continue;

      conditionSet.add(cond);
      map[desig] ??= {};
      map[desig][cond] = (map[desig][cond] || 0) + item.value;
    }

    const conditions = [...conditionSet];
    const maxPerCond = conditions.reduce((acc, cond) => {
      acc[cond] = Math.max(...Object.values(map).map((m) => m[cond] || 0));
      return acc;
    }, {});

    return Object.entries(map).map(([desig, condMap]) => ({
      designation: desig,
      indicators: conditions.map((cond) => ({
        name: cond,
        raw: condMap[cond] || 0,
        value: maxPerCond[cond] ? (condMap[cond] || 0) / maxPerCond[cond] : 0,
      })),
    }));
  }, [clickData, selectedConditions, designations]);

  return {
    radarData,
  };
};


/**
 * Build a simple rows array from clickData:
 *   rows = [{ key, condition, value }]
 * It respects selectedDesignations and selectedConditions (same as charts).
 */
export function useEngagementTableData({
                                         clickData = [],
                                         selectedDesignations = [],
                                         selectedConditions = [],
                                       }) {
  return useMemo(() => {
    const filtered = clickData.filter((it) => {
      const okDesig =
          !selectedDesignations?.length ||
          (it?.user?.designation && selectedDesignations.includes(it.user.designation));
      const okCond =
          !selectedConditions?.length ||
          (it?.conditionName && selectedConditions.includes(it.conditionName));
      return okDesig && okCond;
    });

    const map = new Map(); // conditionName -> count
    for (const it of filtered) {
      const key = it?.conditionName || "(Unspecified)";
      map.set(key, (map.get(key) || 0) + 1);
    }

    const rows = Array.from(map.entries())
        .map(([condition, value]) => ({ key: condition, condition, value }))
        .sort((a, b) => (b.value - a.value) || a.condition.localeCompare(b.condition));

    const total = rows.reduce((s, r) => s + r.value, 0);
    return { rows, total };
  }, [clickData, selectedDesignations, selectedConditions]);
}


// rows: one per entity (condition or designation)
// columns: one per time bucket (latest -> oldest)
export function useEngagementTableMatrixData({
                                               clickData = [],
                                               period = "Day",
                                               groupBy = "condition", // "condition" | "designation"
                                               dateRange,             // [dayjsFrom, dayjsTo]
                                             }) {
  const { splitAndFirstLetterOnlyToUpperCase } = require("../utils/functions").default;

  const keyFromItem = (item) => {
    switch (groupBy) {
      case "condition":
        return item.conditionName || "(Unspecified)";
      case "designation":
        return splitAndFirstLetterOnlyToUpperCase(item?.user?.designation, "_") || "(Unspecified)";
      case "hospital":
        return item?.user?.hospital?.name || "(No hospital)";
      case "district":
        return item?.user?.district?.name || "(No district)";
      case "gender":
        return (item?.user?.gender || "(No gender)").toUpperCase();
      default:
        return "(Unknown)";
    }
  };

  return useMemo(() => {
    if (!clickData?.length || !dateRange?.length) {
      return { columns: [], dataSource: [], meta: { total: 0, buckets: [] } };
    }

    const [from, to] = dateRange;
    const dFrom = dayjs(from);
    const dTo = dayjs(to);

    const periodCfg = {
      Day:   { unit: "day",   label: (d) => d.format("DD MMM"),   key: (d) => d.format("YYYY-MM-DD") },
      Week:  {
      unit: "week", // we still iterate by 'week', but use ISO for labels/keys
         label: (d) => {
         const s = d.startOf("isoWeek");
         const e = d.endOf("isoWeek");
         return `${s.format("D MMM")}–${e.format("D MMM")}`;
       },
         key: (d) => {
         const w = String(d.isoWeek()).padStart(2, "0");
         return `${d.isoWeekYear()}-W${w}`; // e.g. 2025-W09
       },
      },
    Month: { unit: "month", label: (d) => d.format("MMM 'YY"),  key: (d) => d.format("YYYY-MM") },
      Year:  { unit: "year",  label: (d) => d.format("YYYY"),     key: (d) => d.format("YYYY") },
    }[period];

    // Build inclusive buckets across the dateRange
    const bucketDates = Array.from(
        { length: dTo.diff(dFrom, periodCfg.unit) + 1 },
        (_, i) => dFrom.add(i, periodCfg.unit).startOf(periodCfg.unit)
    );

    // Build bucket meta (ascending for now)
    const bucketsAsc = bucketDates.map((d) => ({
      date: d,
      key: periodCfg.key(d),
      label: periodCfg.label(d),
    }));

    // LATEST -> OLDEST in the table
    const buckets = [...bucketsAsc].reverse();

    // Map: entity -> { [bucketKey]: count }
    const entityMap = new Map();
    let grandTotal = 0;

    for (const item of clickData) {
      const ts = dayjs(item.timeStamp);
      // Skip anything outside the selected dateRange just in case
      if (ts.isBefore(dFrom) || ts.isAfter(dTo)) continue;

      const bucketIdxAsc = bucketsAsc.findIndex((b) => ts.isSame(b.date, period.toLowerCase()));
      if (bucketIdxAsc < 0) continue;

      const entity = keyFromItem(item);

      if (!entityMap.has(entity)) entityMap.set(entity, {});
      const bucketKey = bucketsAsc[bucketIdxAsc].key;
      entityMap.get(entity)[bucketKey] = (entityMap.get(entity)[bucketKey] || 0) + 1;
      grandTotal += 1;
    }

    // Build AntD columns: entity column + one per bucket (latest -> oldest)
    const entityTitle =
        groupBy === "condition" ? "Condition" :
            groupBy === "designation" ? "Designation" :
                groupBy === "hospital" ? "Hospital" :
                    groupBy === "district" ? "District" :
                        "Gender";

    const columns = [
      {
        title: entityTitle,
        dataIndex: "entity",
        key: "entity",
        fixed: "left",
        ellipsis: true,
        sorter: (a, b) => String(a.entity).localeCompare(String(b.entity)),
      },
      ...buckets.map((b) => ({
        title: b.label,
        dataIndex: b.key,
        key: b.key,
        align: "right",
        sorter: (a, c) => (a[b.key] || 0) - (c[b.key] || 0),
        defaultSortOrder: b === buckets[0] ? "descend" : undefined, // emphasize latest by default
        width: 110,
        responsive: ["sm"],
      })),
      {
        title: "Total",
        dataIndex: "rowTotal",
        key: "rowTotal",
        align: "right",
        sorter: (a, b) => a.rowTotal - b.rowTotal,
        defaultSortOrder: "descend",
        width: 110,
      },
    ];

    // Rows from entityMap (fill missing buckets with 0)
    const dataSource = Array.from(entityMap.entries()).map(([entity, byBucket]) => {
      const row = { key: entity, entity };
      let sum = 0;
      for (const b of buckets) {
        const v = byBucket[b.key] || 0;
        row[b.key] = v;
        sum += v;
      }
      row.rowTotal = sum;
      return row;
    });

    // Sort rows by total desc initially
    dataSource.sort((a, b) => b.rowTotal - a.rowTotal || String(a.entity).localeCompare(String(b.entity)));

    return {
      columns,
      dataSource,
      meta: {
        total: grandTotal,
        buckets, // [{date,key,label}] latest->oldest
      },
    };
  }, [clickData, dateRange, period, groupBy]);
}
